/**
 * Country → regional defaults for the onboarding setup wizard.
 *
 * One country choice derives four organization settings (currency, number
 * format, date format, timezone). The user can always adjust each derived
 * field; this map only provides sensible starting points.
 *
 * Constraints (keep in sync):
 * - `currency` must be a key of CURRENCIES (lib/platform/utils/currency.ts).
 *   Countries whose local currency is not supported get EUR (or USD where
 *   clearly a dollar market).
 * - `timezone` must exist in TIMEZONES (lib/platform/utils/timezones.ts).
 * - `numberFormat` / `dateFormat` values match the settings page options.
 */

import type { CurrencyCode } from '@/lib/platform/utils/currency';

export type NumberFormat = 'eu' | 'us' | 'fr';
export type DateFormat = 'DD-MM-YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

export interface CountryDefaults {
  currency: CurrencyCode;
  numberFormat: NumberFormat;
  dateFormat: DateFormat;
  timezone: string;
}

/** Platform-wide fallback (matches the organization table column defaults) */
export const DEFAULT_REGIONAL_SETTINGS: CountryDefaults = {
  currency: 'EUR',
  numberFormat: 'eu',
  dateFormat: 'DD-MM-YYYY',
  timezone: 'Europe/Copenhagen',
};

export const COUNTRY_DEFAULTS: Record<string, CountryDefaults> = {
  // Nordics
  DK: {
    currency: 'DKK',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Europe/Copenhagen',
  },
  SE: {
    currency: 'SEK',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Europe/Stockholm',
  },
  NO: { currency: 'NOK', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Oslo' },
  FI: {
    currency: 'EUR',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Europe/Helsinki',
  },
  IS: {
    currency: 'EUR',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Atlantic/Reykjavik',
  }, // ISK not supported

  // Western & Central Europe
  DE: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Berlin' },
  AT: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Vienna' },
  CH: { currency: 'CHF', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Zurich' },
  NL: {
    currency: 'EUR',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Europe/Amsterdam',
  },
  BE: {
    currency: 'EUR',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Europe/Brussels',
  },
  LU: {
    currency: 'EUR',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Europe/Brussels',
  },
  FR: { currency: 'EUR', numberFormat: 'fr', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Paris' },
  IT: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Rome' },
  ES: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Madrid' },
  PT: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Lisbon' },
  IE: { currency: 'EUR', numberFormat: 'us', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Dublin' },
  GB: { currency: 'GBP', numberFormat: 'us', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/London' },

  // Eastern Europe (local currencies not supported → EUR)
  PL: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Warsaw' },
  CZ: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Prague' },
  HU: {
    currency: 'EUR',
    numberFormat: 'eu',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Europe/Budapest',
  },
  GR: { currency: 'EUR', numberFormat: 'eu', dateFormat: 'DD-MM-YYYY', timezone: 'Europe/Athens' },

  // North America
  US: {
    currency: 'USD',
    numberFormat: 'us',
    dateFormat: 'MM/DD/YYYY',
    timezone: 'America/New_York',
  },
  CA: {
    currency: 'CAD',
    numberFormat: 'us',
    dateFormat: 'MM/DD/YYYY',
    timezone: 'America/Toronto',
  },

  // Oceania
  AU: {
    currency: 'AUD',
    numberFormat: 'us',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Australia/Sydney',
  },
  NZ: {
    currency: 'USD',
    numberFormat: 'us',
    dateFormat: 'DD-MM-YYYY',
    timezone: 'Pacific/Auckland',
  }, // NZD not supported
};

/** Defaults for a country code (case-insensitive); platform fallback when unknown. */
export function getCountryDefaults(countryCode?: string | null): CountryDefaults {
  if (!countryCode) return DEFAULT_REGIONAL_SETTINGS;
  return COUNTRY_DEFAULTS[countryCode.toUpperCase()] ?? DEFAULT_REGIONAL_SETTINGS;
}
